/*
 * Copyright (C) 2013 Spanning Cloud Apps.  All rights reserved.
 */
/**
 * Service module for common batch services/utilities.
 */
'use strict';

angular
  .module('services.batch', ['http.batch', 'http.queue', 'services.cache', 'sbsf.enums'])
  /**
   * A factory with common batch utilities.
   */
  .factory('batchUtils', [
    '$q',
    '$filter',
    '$rootScope',
    'httpBatch',
    'httpQueue',
    'CacheService',
    'VIEW_STATES',
    'JOB_TYPE',
    function ($q, $filter, $rootScope, httpBatch, httpQueue, CacheService, VIEW_STATES, JOB_TYPE) {
      return {
        /**
         * Utility for getting a list batches.
         *
         * @param {number} limit - The max number of results to be returned.
         * @param {boolean} [refresh] - flag to return cached version or send a request to get batches
         *
         * @returns {Promise} - A Promise that resolves into the list of batches.
         */
        getBatches: function (limit, refresh) {
          var me = this;

          // Request batches from server when batches is empty, refresh is true, or when the current count != limit passed in, or when the count is 0
          // If the count is 0 we most likely came from the start state when the client has completed their first backup and we need to navigate out of that state.
          if (_.isEmpty(this.batches) || refresh || this.batches.rows.length !== limit || this.batches.count === 0) {
            return httpBatch.getBatches(limit).then(function (batches) {
              me.batches = batches;
              return batches;
            });
          } else {
            // return caches version wrapped in promise
            const deferred = $q.defer();
            deferred.resolve(this.batches);
            return deferred.promise;
          }
        },

        /**
         * Utility for getting the percent complete for a batch.  It will round to the
         * nearest integer.
         *
         * As of 9/11/2014 this should no longer compute a percent on its own.  Instead, it should use
         * the percentage handed back from the server.  I decided to leave this function here just in
         * case the batch object passed in doesn't include the percentComplete field (somehow).  If it
         * doesn't, we should start to see weird behavior where the percent is 0 but it clearly became
         * a partial/successful batch.
         *
         * @param {object} batch - A batch object.
         * @param {number} batch.total - The total number of items processed.
         * @param {number} batch.itemsCompleted - The number of items completed in the batch.
         * @returns {number} The percent as a whole number.  If no items were completed or part of the
         *  batch 0 is returned.
         */
        getBatchPercent: function (batch) {
          batch = batch || {};
          return !batch.percentComplete ? 0 : batch.percentComplete;
        },

        /**
         * Utility for loading a batch by id and populating the currentBatch object with the
         * retrieved value.
         * @param {Number} batchId - The id of the batch to be loaded.
         * @param {Boolean} [skipSettingCurrentBatch=false] - Flag to skip setting the current batch.
         * @return {Object} - The resulting value of the currentBatch object.
         */
        loadBatch: function (batchId, skipSettingCurrentBatch) {
          var me = this,
            skipSetting = skipSettingCurrentBatch || false;
          return this.sendBatchRequest(httpBatch.getBatch(batchId)).then(function (batch) {
            if (!skipSetting) {
              me.currentBatch = batch;
            }
            return batch;
          });
        },

        /**
         * Utility for sending request to get batch. Handle the increment and decrement of requestsInProgress in one place.
         *
         * @param {Promise} batchRequest - A promise that resolves to the request being sent.
         *
         * @returns {Promise} - A Promise that resolves into a batch
         *   as well as updating the currentBatch value.  Returns undefined if
         *   there is no batch.
         */
        sendBatchRequest: function (batchRequest) {
          this.requestsInProgress++;
          return batchRequest.finally(() => {
            if (this.requestsInProgress > 0) {
              this.requestsInProgress--;
            }
          });
        },

        /**
         * Utility for getting the next batch after the provided batch id.
         *
         * @param {Number} [startBatchId=this.currentBatch.batchId] - Optional batch id to start at. Defaults to current batch id.
         *
         * @returns {Promise} - A Promise that resolves into the next batch
         *   as well as updating the currentBatch value.  Returns undefined if
         *   there is no next batch.
         */
        getNextBatch: function (startBatchId) {
          var me = this,
            batchId = startBatchId || this.currentBatch.batchId;

          if (_.isNumber(batchId)) {
            return this.sendBatchRequest(httpBatch.getNextBatch(batchId)).then(function (batch) {
              if (batch) {
                me.currentBatch = batch;
              }
              return me.currentBatch;
            });
          }
        },

        /**
         * Utility for getting the previous batch before the provided batch id.
         *
         * @param {Number} [startBatchId=this.currentBatch.batchId] - Optional batch id to start at. Defaults to current batch id.
         *
         * @returns {Promise} - A Promise that resolves into the previous batch
         *   as well as updating the currentBatch value.  Returns undefined if
         *   there is no next batch.
         */
        getPreviousBatch: function (startBatchId) {
          var me = this,
            batchId = startBatchId || this.currentBatch.batchId;

          if (_.isNumber(batchId)) {
            return this.sendBatchRequest(httpBatch.getPreviousBatch(batchId)).then(function (batch) {
              if (batch) {
                me.currentBatch = batch;
              }
              return me.currentBatch;
            });
          }
        },

        /**
         * Utility function to get the latest batch on a particular date.
         *
         * @param {Date} batchDate - The date to specify what day to search.
         * @returns {Promise} - A Promise that resolves into the latest batch
         *   on the date provided as well as updating the currentBatch value.
         *   Returns undefined if there is no batch for the date.
         */
        getBatchByDate: function (batchDate) {
          var me = this;
          if (batchDate) {
            return this.sendBatchRequest(httpBatch.getBatchByDate(batchDate)).then(function (batch) {
              if (batch) {
                me.currentBatch = batch;
              } else {
                me.currentBatch = {
                  noBackup: true,
                  date: batchDate
                };
              }
              return batch;
            });
          }
        },

        /**
         * Function use to get the most recent backup and set it as the currentBatch.
         */
        getLatestBackup: function () {
          var me = this;
          return this.sendBatchRequest(httpBatch.getLatestBackup()).then(function (batch) {
            if (batch) {
              me.currentBatch = batch;
            }
            return batch;
          });
        },

        /**
         * Utility function to send request to generate and download a csv of restore batch details.
         *
         * @param {Number} batchId - The batch id for the csv.
         *
         * TODO: Fix return object when service is done and we know what kind of object is returned
         * @returns {Promise} - A promise that resolves to an object.
         */
        //TODO: Unit test for this service is in place
        downloadBulkRestoreCsv: function (batchId) {
          var params = {
            batchId: batchId
          };

          return httpBatch.getCsv(params).then(function (result) {
            return result;
          });
        },

        /**
         * Utility function to send request to download the file attached to the batch (export or csv bulkrestore preview).
         *
         * @param {Number} batchId - The batch id for the csv.
         *
         * @returns {Promise} - A promise that resolves to an object.
         */
        getDownloadBatchUrl: function (batchId) {
          return httpBatch.getDownloadBatchUrl(batchId).then(function (result) {
            return result;
          });
        },

        /**
         * Utility function to send request to queue message for bulk restore preview of failed records.
         *
         * @param {Number} batchId - The batch id for the csv.
         *
         * @returns {Promise} - A promise that resolves to an object.
         */
        bulkRestoreFailedRecords: function (batchId) {
          var params = {
            batchId: batchId
          };

          return httpQueue.submitBulkRestoreFailedRecords(params).then(function (result) {
            return result;
          });
        },

        /**
         * Used to get the current batch date in a title format.
         *
         * @returns {string} - The date formatted for titles.
         */
        getTitleBatchDate: function () {
          var batchDate = moment(this.currentBatch.date);

          return $filter('i18n')('BATCH_DATE_TITLE', [
            batchDate.format(SBSF.messages.M_D_Y),
            batchDate.format(SBSF.messages.H_M)
          ]);
        },

        /**
         * Utility used to determine if a batch is an export or not.
         *
         * @param {object} batch - The batch object
         * @returns {boolean} - Flag if the batch is an export or not
         */
        isBatchExportType: function (batch) {
          return batch.type === 'EXPORT' || batch.type === 'BULK_RESTORE_PREVIEW';
        },

        /**
         * Utility used to determine if a batch is a bulk restore or not.
         *
         * @param {object} batch - The batch object
         * @returns {boolean} - Flag if the batch is a bulk restore or not
         */
        isBatchBulkRestoreType: function (batch) {
          return batch.type === 'BULK_RESTORE' || batch.type === 'CROSS_ORG_RESTORE';
        },

        /**
         * Used to get the errors for a batch.
         *
         * @param {number} batchId - The id of the batch.
         * @returns {promise} - A promise that resolves to a error code and count object.
         */
        getEventCountForBatch: function (batchId) {
          return httpBatch.getEventCountForBatch(batchId).then(function (result) {
            return result;
          });
        },

        /**
         * Used to get a page of event records for a batch.
         *
         * @param {number} batchId - The id of the batch.
         * @param {number} [eventCode] - The event code of the records to retrieve.
         * @param {number} [offset=0] - Optional. The page of records to get.
         * @param {number} [limit=10] - Optional. The limit of the number of records to get.
         * @returns {promise} - A promise that resolves to a list of errored records.
         */
        getBatchEvents: function (batchId, eventCode, offset, limit) {
          var params = {
            batchId: batchId,
            eventCode: eventCode,
            pageSize: limit || 10,
            pageNumber: offset || 0
          };

          return this.sendBatchRequest(
            httpBatch.getBatchEvents(params).then(function (result) {
              return result;
            })
          );
        },

        /**
         * Used to get a page of detailed failures for a batch-level restore error.
         *
         * @param {number} batchId - The id of the batch.
         * @param {number} [offset=0] - Optional. The page of records to get.
         * @param {number} [limit=10] - Optional. The limit of the number of records to get.
         * @returns {Promise} - A promise that resolves to an object containing a count and a result array. The count is the
         * total number of records, but the result array will only contain the current page of records.
         */
        getBatchLevelRestoreFailures: function (batchId, offset, limit) {
          var params = {
            batchId: batchId,
            pageSize: limit || 10,
            page: offset || 0
          };

          return this.sendBatchRequest(
            httpBatch.getBatchLevelRestoreFailures(params).then(function (result) {
              return result;
            })
          );
        },

        /**
         * Used to get a page of metadata records for a batch.
         *
         * @param {number} batchId - The id of the batch.
         * @param {number} componentType - The component type of the records to retrieve.
         * @param {number} [offset=0] - Optional. The page of records to get.
         * @param {number} [limit=10] - Optional. The limit of the number of records to get.
         * @returns {promise} - A promise that resolves to a list of metadata records.
         */
        getMetadataRecords: function (batchId, componentType, offset, limit) {
          var params = {
            batchId: batchId,
            componentType: componentType,
            pageSize: limit || 10,
            page: offset || 0
          };

          return this.sendBatchRequest(
            httpBatch.getMetadataBatchRecords(params).then(function (result) {
              return result.rows;
            })
          );
        },

        /**
         * Utility for getting a list of backup batch dots.
         *
         * @param {number} limit - The max number of results to be returned.
         * @param {boolean} [refresh] - flag to return cached version or send a request to get batches
         *
         * @returns {Promise} - A Promise that resolves into the list of batches.
         */
        getBackupBatchDots: function (limit, refresh) {
          var me = this;

          // Request batch dots from server when backupBatchDots is empty, refresh is true, or when the current count != limit passed in, or when the count is 0
          // If the count is 0 we most likely came from the start state when the client has completed their first backup and we need to navigate out of that state.
          if (
            _.isEmpty(this.backupBatchDots) ||
            refresh ||
            this.backupBatchDots.rows.length !== limit ||
            this.backupBatchDots.count === 0
          ) {
            return httpBatch.getBackupBatchDots(limit).then(function (batches) {
              me.backupBatchDots = batches;
              me.backupBatchDots.count = batches.rows.length;
              return batches;
            });
          } else {
            // return cached version wrapped in promise
            const deferred = $q.defer();
            deferred.resolve(this.backupBatchDots);
            return deferred.promise;
          }
        },

        /**
         * Utility for getting a list of backup batch dots.
         *
         * @param {number} limit - The max number of results to be returned.
         * @param {boolean} [refresh] - flag to return cached version or send a request to get batches
         *
         * @returns {Promise} - A Promise that resolves into the list of batches.
         */
        getBatchPrimaryObjectCounts: function (limit, refresh) {
          var me = this;

          // Request counts from server when batchPrimaryObjectCounts is empty, refresh is true, or when the current count != limit passed in, or when the count is 0
          // If the count is 0 we most likely came from the start state when the client has completed their first backup and we need to navigate out of that state.
          if (
            _.isEmpty(this.batchPrimaryObjectCounts) ||
            refresh ||
            this.batchPrimaryObjectCounts.length !== limit ||
            this.batchPrimaryObjectCounts.length === 0
          ) {
            return httpBatch.getBatchPrimaryObjectCounts(limit).then(function (batches) {
              me.batchPrimaryObjectCounts = batches;
              return batches;
            });
          } else {
            // return cached version wrapped in promise
            const deferred = $q.defer();
            deferred.resolve(this.batchPrimaryObjectCounts);
            return deferred.promise;
          }
        },

        /**
         * Utility function to get all the backups starting within a date range.
         *
         * @param {Date} start - The start date/time, inclusive, of the range to search.
         * @param {Date} end - The end date/time, inclusive, of the range to search.
         *
         * @returns {Promise} - A Promise that resolves into the list of batches.
         */
        getBackupsByDateRange: function (start, end) {
          if (start && end) {
            // TODO Not sure if this needs to update the cached me.batches or me.currentBatch values or if it should stay
            // separate, maybe with its own cache.
            return this.sendBatchRequest(httpBatch.getBackupsByDateRange(start, end));
          }
          // TODO Not sure if we need to resolve with an empty array when there is a missing start or end date. getBatchByDate() doesn't.
        },

        /**
         * Returns backup batches that were started in the specified month. This one differs from getBackupsByDateRange in
         * that it doesn't join the details.
         *
         * @param {Moment} monthMoment - The moment representing the month.
         * @returns {Promise<[]>} the promise of the list of BatchInfo objects for the matching batches.
         */
        getBackupsByMonth: function (monthMoment) {
          // The function name plus the year and month string is the cache key.
          const yearAndMonth = monthMoment.format('YYYY-MM');
          const cacheKey = `getBackupsByMonth:${yearAndMonth}`;
          return CacheService.getResult(cacheKey, () =>
            this.sendBatchRequest(httpBatch.getBackupsByMonth(yearAndMonth))
          );
        },

        /**
         * Returns the oldest and newest backups.
         *
         * @returns {Promise} the promise of the backup details.
         */
        getBackupDetails: function () {
          // The function name is the cache key.
          const cacheKey = 'getBackupDetails';
          return CacheService.getResult(cacheKey, () => this.sendBatchRequest(httpBatch.getBackupDetails()));
        },

        getParentJobTypeForCsvVerification: function (bulkRestoreCsvVerificationData) {
          return bulkRestoreCsvVerificationData.parentJobType || JOB_TYPE.BULK_RESTORE;
        },

        /**
         * @param {JobType} parentJobType
         * @return {string} - state to go after csv verification
         */
        getStateToGoAfterCsvVerification: function (parentJobType) {
          switch (parentJobType) {
            case JOB_TYPE.BULK_RESTORE:
              return VIEW_STATES.BULK_RESTORE_ENTER;
            case JOB_TYPE.EXPORT:
              return VIEW_STATES.BULK_EXPORT_ENTER;
            default:
              throw new Error('Unknown parent job type');
          }
        },

        // Keep track of requests in progress
        requestsInProgress: 0,

        // TODO: Remove this after the all activity stops pulling/caching entire batch data
        // Current batches object returned from getBatches.
        // {
        //  count: 0
        //  rows: []
        // }
        batches: {},

        // Current object returned from getBackupBatchDots.
        // {
        //  count: 0
        //  rows: []
        // }
        backupBatchDots: {},

        batchPrimaryObjectCounts: {},

        // Value used to track a selected batch between views.
        currentBatch: {},

        // The initial object type, if any, to be shown when the current batch is loaded.
        initialObjectType: undefined,

        // Static object used to get data related to batch from the status.
        STATUS: {
          // success
          SUCCESS: {
            batchMsgKey: 'BATCH_SUCCESS_MSG',
            bigBoinkCls: 'sca-icon-green-shadow',
            boinkCls: 'sbsf-boink-success sca-icon-green',
            textCls: 'sbsf-green'
          },
          // partial
          PARTIAL: {
            batchMsgKey: 'BATCH_PARTIAL_MSG',
            bigBoinkCls: 'sca-icon-partial',
            boinkCls: 'sbsf-boink-partial sca-icon-gray',
            textCls: 'sbsf-gray'
          },
          // failure
          FAILURE: {
            batchMsgKey: 'BATCH_FAILURE_MSG',
            bigBoinkCls: 'sca-icon-red-shadow',
            boinkCls: 'sbsf-boink-failure sca-icon-red',
            textCls: 'sbsf-red'
          },
          // No batch
          NONE: {
            boinkCls: 'sbsf-boink-inactive'
          }
        },

        // Static object used to get data related to batch from the status for mobile.
        STATUS_MOBILE: {
          // success
          SUCCESS: {
            batchMsgKey: 'BATCH_SUCCESS_MSG',
            bigBoinkCls: 'sca-icon-green-shadow',
            boinkCls: 'sbsf-boink-success sca-icon-green',
            textCls: 'sbsf-green'
          },
          // partial
          PARTIAL: {
            batchMsgKey: 'BATCH_PARTIAL_MSG',
            bigBoinkCls: 'sca-icon-partial',
            boinkCls: 'sbsf-boink-partial sca-icon-gray',
            textCls: 'sbsf-gray'
          },
          // failure
          FAILURE: {
            batchMsgKey: 'BATCH_FAILURE_MSG',
            bigBoinkCls: 'sca-icon-red-shadow',
            boinkCls: 'sbsf-boink-failure sca-icon-red',
            textCls: 'sbsf-red'
          },
          // No batch
          NONE: {
            boinkCls: 'sca-icon-hollow-mini-boink-r'
          }
        },

        // Event type enum.
        EVENT_TYPES: {
          WARNING: 'WARNING',
          ERROR: 'ERROR'
        }
      };
    }
  ]);
